<script setup>
import { ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import SegmentsAPI from 'dashboard/api/segments';

const EXAMPLE = {
  match: 'all',
  rules: [
    { type: 'attribute', field: 'additional_attributes.medup_sex_name', op: 'eq', value: 'Ж' },
    { type: 'operation', name: 'DealCompleted', op: 'none', within_days: 180 },
  ],
};

const segments = ref([]);
const loading = ref(false);
const editing = ref(null); // { id?, name, kind, definitionText }
const previewCount = ref(null);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await SegmentsAPI.get();
    segments.value = data || [];
  } catch (e) {
    useAlert('Не удалось загрузить сегменты');
  } finally {
    loading.value = false;
  }
};
onMounted(load);

const startNew = () => {
  editing.value = {
    name: '',
    kind: 'dynamic',
    definitionText: JSON.stringify(EXAMPLE, null, 2),
  };
  previewCount.value = null;
};

const startEdit = s => {
  editing.value = {
    id: s.id,
    name: s.name,
    kind: s.kind,
    definitionText: JSON.stringify(s.definition || {}, null, 2),
  };
  previewCount.value = null;
};

const parseDef = () => {
  try {
    return JSON.parse(editing.value.definitionText);
  } catch (e) {
    useAlert('Определение — некорректный JSON');
    return null;
  }
};

const preview = async () => {
  const definition = parseDef();
  if (!definition) return;
  try {
    const { data } = await SegmentsAPI.preview(definition);
    previewCount.value = data.count;
  } catch (e) {
    useAlert('Ошибка предпросмотра определения');
  }
};

const save = async () => {
  const definition = parseDef();
  if (!definition) return;
  const payload = {
    name: editing.value.name,
    kind: editing.value.kind,
    definition,
  };
  try {
    if (editing.value.id) {
      await SegmentsAPI.update(editing.value.id, payload);
    } else {
      await SegmentsAPI.create(payload);
    }
    editing.value = null;
    await load();
  } catch (e) {
    useAlert('Не удалось сохранить сегмент');
  }
};

const recompute = async id => {
  try {
    await SegmentsAPI.recompute(id);
    useAlert('Пересчёт запущен');
  } catch (e) {
    useAlert('Не удалось запустить пересчёт');
  }
};

const remove = async id => {
  if (!window.confirm('Удалить сегмент?')) return;
  await SegmentsAPI.delete(id);
  await load();
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1">
    <div class="w-full px-6 py-6">
      <div class="flex items-center justify-between mb-1">
        <h1 class="text-xl font-medium text-n-slate-12">Сегменты</h1>
        <button
          class="px-3 py-1.5 text-sm text-white rounded-lg bg-n-brand"
          @click="startNew"
        >
          + Сегмент
        </button>
      </div>
      <p class="mb-4 text-sm text-n-slate-11">
        Аудитории по условиям над профилем и потоком событий. Динамические пересчитываются.
      </p>

      <div
        v-if="editing"
        class="p-4 mb-6 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="flex gap-2 mb-3">
          <input
            v-model="editing.name"
            placeholder="Имя сегмента"
            class="flex-1 px-2 py-1.5 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12"
          />
          <select
            v-model="editing.kind"
            class="px-2 py-1.5 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12"
          >
            <option value="dynamic">динамический</option>
            <option value="static">статический</option>
          </select>
        </div>
        <textarea
          v-model="editing.definitionText"
          rows="12"
          spellcheck="false"
          class="w-full px-3 py-2 font-mono text-xs border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12"
        ></textarea>
        <div class="flex items-center gap-2 mt-3">
          <button
            class="px-3 py-1.5 text-sm border rounded-lg border-n-weak text-n-slate-11"
            @click="preview"
          >
            Предпросмотр
          </button>
          <span v-if="previewCount !== null" class="text-sm text-n-slate-12">
            Подходит: <b>{{ previewCount }}</b>
          </span>
          <span class="flex-1"></span>
          <button
            class="px-3 py-1.5 text-sm border rounded-lg border-n-weak text-n-slate-11"
            @click="editing = null"
          >
            Отмена
          </button>
          <button
            class="px-3 py-1.5 text-sm text-white rounded-lg bg-n-brand"
            @click="save"
          >
            Сохранить
          </button>
        </div>
      </div>

      <div v-if="loading" class="py-10 text-center text-n-slate-11">Загрузка…</div>
      <div v-else-if="!segments.length" class="py-10 text-center text-n-slate-11">
        Сегментов пока нет.
      </div>
      <div v-else class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Имя</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Тип</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Размер</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Пересчитан</th>
              <th class="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in segments"
              :key="s.id"
              class="border-b border-n-weak last:border-0"
            >
              <td class="px-3 py-2 font-medium text-n-slate-12">{{ s.name }}</td>
              <td class="px-3 py-2 text-n-slate-11">{{ s.kind }}</td>
              <td class="px-3 py-2 tabular-nums text-n-slate-11">{{ s.member_count }}</td>
              <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                {{ s.last_computed_at ? String(s.last_computed_at).slice(0, 16) : '—' }}
              </td>
              <td class="px-3 py-2 text-right whitespace-nowrap">
                <button
                  class="px-2 py-1 text-xs border rounded border-n-weak text-n-slate-11"
                  @click="recompute(s.id)"
                >
                  Пересчитать
                </button>
                <button
                  class="px-2 py-1 ml-1 text-xs border rounded border-n-weak text-n-slate-11"
                  @click="startEdit(s)"
                >
                  Изменить
                </button>
                <button
                  class="px-2 py-1 ml-1 text-xs border rounded border-n-weak text-n-ruby-11"
                  @click="remove(s.id)"
                >
                  Удалить
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
